import { MinimalShipment, Shipment, ShipmentType, TourEvent } from '@/types/delivery-management'
import { destinationAddress } from '@/utils/delivery-management'

const shipmentTypeBadges = {
    [ShipmentType.Delivery]: 'D',
    [ShipmentType.Return]: 'R',
    [ShipmentType.Pickup]: 'P',
} as const

export function getShipmentTypeBadge(type: ShipmentType): string {
    return shipmentTypeBadges[type] || type?.toString().charAt(0).toUpperCase() || '?'
}

export interface GroupedTourEvents<TE = TourEvent> {
    groups: TE[][]
    consolidatedGroups: number[]
}

/**
 * Groups the given {tourEvents} by their shipment address
 * When {shouldConsolidate} is true it'll also consolidate the events are the shipment parameters matches
 * {groups} will contain the list of groups with their tour events
 * {consolidatedGroups} will contain the indexes of the groups that are consolidated
 */
export function groupTourEvents<TE extends TourEvent<MinimalShipment | Shipment>>(
    tourEvents: TE[],
    shouldConsolidate = false,
): GroupedTourEvents<TE> {
    const groups: TE[][] = []
    const consolidatedGroups: number[] = []
    let isConsolidatedGroup = false

    for (const event of tourEvents) {
        if (event.shipment && groups.length > 0) {
            let currentGroupIndex = groups.length - 1
            const groupShipment = groups[currentGroupIndex][0].shipment

            if (shouldConsolidate) {
                const eventConsolidateParameters = consolidationParameters(event)
                const groupConsolidateParameters = groupShipment
                    ? consolidationParameters(groups[currentGroupIndex][0])
                    : []

                // If consolidation parameters match, add to current group
                if (arraysEqual(eventConsolidateParameters, groupConsolidateParameters)) {
                    groups[currentGroupIndex].push(event)

                    isConsolidatedGroup = true
                    if (!consolidatedGroups.includes(currentGroupIndex)) {
                        consolidatedGroups.push(currentGroupIndex)
                    }
                    continue
                }

                // If consolidation parameters match the last event in last group
                const lastEvent = groups[groups.length - 1][groups[groups.length - 1].length - 1]
                const lastConsolidateParameters = lastEvent.shipment
                    ? consolidationParameters(lastEvent)
                    : []

                if (arraysEqual(eventConsolidateParameters, lastConsolidateParameters)) {
                    const lastGroup = groups[groups.length - 1]
                    const removedLastEvent = lastGroup.pop()!

                    groups.push([removedLastEvent, event])
                    currentGroupIndex = groups.length - 1
                    isConsolidatedGroup = true
                    if (!consolidatedGroups.includes(currentGroupIndex)) {
                        consolidatedGroups.push(currentGroupIndex)
                    }
                    continue
                }
            }

            const groupAddress = groupShipment ? destinationAddress(groupShipment) : ''
            const shipmentAddress = event.shipment ? destinationAddress(event.shipment) : ''

            if (shipmentAddress === groupAddress && !isConsolidatedGroup) {
                groups[currentGroupIndex].push(event)
                continue
            }
        }

        isConsolidatedGroup = false
        groups.push([event])
    }

    return { groups, consolidatedGroups }
}

export function arraysEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) return false
    return arr1.every((value, index) => value === arr2[index])
}

export function consolidationParameters(
    tourEvent: TourEvent<MinimalShipment | Shipment>,
): string[] {
    return [
        tourEvent.shipment?.sendingLocation?.id ?? '',
        tourEvent.shipment?.receivingLocation?.id ?? '',
        // We'll need to match on haulier and shipper as well as those affect to packaging transaction
        tourEvent.shipment?.haulierLocation?.id ?? '',
        tourEvent.shipment?.shipperLocation?.id ?? '',
        tourEvent.type.toString(),
    ]
}
